import api from './base';
import {
  WalletBalance,
  WalletTransaction,
  WithdrawalRequest,
  TransactionFilters,
  PaginatedTransactions,
  PaginatedWithdrawals,
  WalletStats,
  WalletAnalytics,
  DepositData,
  DepositInitiation,
  WithdrawalData,
  TransferData,
  TransferResult,
  WalletSettingsData,
  UserWallet,
  Bank,
  AccountVerification,
  UserSearchResult,
  Currency,
} from '@/types/wallet';

export const walletApi = {
  getBalance: async (currency?: Currency) => {
    // Backend route is just /balance, currency is passed as query parameter
    return api.get<{ success: boolean; data: WalletBalance }>('/wallet/balance', {
      params: currency ? { currency } : {}
    });
  },

  getAllBalances: async () => {
    return api.get<{ success: boolean; data: WalletBalance[] }>('/wallet/balances');
  },

  getTransactions: async (filters: TransactionFilters = {}, page = 1, limit = 20) => {
    return api.get<{ success: boolean; data: PaginatedTransactions }>('/wallet/transactions', {
      params: { ...filters, page, limit },
    });
  },

  getTransaction: async (id: string) => {
    return api.get<{ success: boolean; data: WalletTransaction }>(`/wallet/transactions/${id}`);
  },

  exportTransactions: async (filters: TransactionFilters = {}, format: 'csv' | 'pdf' = 'csv') => {
    return api.post<Blob>(
      '/wallet/transactions/export',
      { ...filters, format },
      { responseType: 'blob' }
    );
  },

  initiateDeposit: async (data: DepositData) => {
    return api.post<{ success: boolean; data: DepositInitiation }>('/wallet/deposit', data);
  },

  verifyDeposit: async (reference: string) => {
    return api.post<{ success: boolean; data: WalletTransaction }>('/wallet/deposit/verify', { reference });
  },

  getDepositHistory: async (page = 1, limit = 20) => {
    return api.get<{ success: boolean; data: PaginatedTransactions }>('/wallet/deposits', {
      params: { page, limit },
    });
  },

  getDeposit: async (id: string) => {
    return api.get<{ success: boolean; data: WalletTransaction }>(`/wallet/deposits/${id}`);
  },

  requestWithdrawal: async (data: WithdrawalData) => {
    return api.post<{ success: boolean; data: WithdrawalRequest }>('/wallet/withdraw', data);
  },

  calculateFeePreview: async (data: {
    amount: number;
    type: 'deposit' | 'withdrawal';
    method: 'paystack' | 'bank_transfer' | 'mobile_money' | 'internal_transfer';
    provider?: string;
    currency?: string;
  }) => {
    return api.post<{ success: boolean; data: { finalFee: number; netAmount: number } }>(
      '/wallet/fees/calculate-preview',
      data
    );
  },

  getDepositConfig: async (currency?: Currency) => {
    return api.get<{ success: boolean; data: any }>('/wallet/fees/display', {
      params: currency ? { currency } : {}
    });
  },

  getWithdrawalHistory: async (page = 1, limit = 20) => {
    return api.get<{ success: boolean; data: PaginatedWithdrawals }>('/wallet/withdrawals', {
      params: { page, limit },
    });
  },

  getWithdrawal: async (id: string) => {
    return api.get<{ success: boolean; data: WithdrawalRequest }>(`/wallet/withdrawals/${id}`);
  },

  cancelWithdrawal: async (id: string) => {
    return api.delete<{ success: boolean; message: string }>(`/wallet/withdrawals/${id}`);
  },

  transferFunds: async (data: TransferData) => {
    return api.post<{ success: boolean; data: TransferResult }>('/wallet/transfer', data);
  },

  getTransferHistory: async (page = 1, limit = 20) => {
    return api.get<{ success: boolean; data: PaginatedTransactions }>('/wallet/transfers', {
      params: { page, limit },
    });
  },

  getWalletSettings: async () => {
    return api.get<{ success: boolean; data: UserWallet }>('/wallet/settings');
  },

  updateWalletSettings: async (data: WalletSettingsData) => {
    return api.put<{ success: boolean; data: UserWallet }>('/wallet/settings', data);
  },

  getWalletStats: async () => {
    return api.get<{ success: boolean; data: WalletStats }>('/wallet/stats');
  },

  getWalletAnalytics: async (startDate?: string, endDate?: string) => {
    return api.get<{ success: boolean; data: WalletAnalytics }>('/wallet/analytics', {
      params: { startDate, endDate },
    });
  },

  getBankList: async (country = 'ghana', currency: Currency = 'GHS') => {
    return api.get<{ success: boolean; data: Bank[] }>('/wallet/banks', {
      params: { country, currency },
    });
  },

  verifyBankAccount: async (accountNumber: string, bankCode: string) => {
    return api.post<{ success: boolean; data: AccountVerification }>(
      '/wallet/verify-bank-account',
      { accountNumber, bankCode }
    );
  },

  searchUsers: async (query: string) => {
    return api.get<{ success: boolean; data: UserSearchResult[] }>('/wallet/users/search', {
      params: { query },
    });
  },

  addBankAccount: async (data: {
    accountName: string;
    accountNumber: string;
    bankName: string;
    bankCode: string;
    branchCode?: string;
    isDefault?: boolean;
  }) => {
    return api.post<{ success: boolean; data: UserWallet }>('/wallet/bank-accounts', data);
  },

  removeBankAccount: async (accountId: string) => {
    return api.delete<{ success: boolean; message: string }>(
      `/wallet/bank-accounts/${accountId}`
    );
  },

  setDefaultBankAccount: async (accountId: string) => {
    return api.put<{ success: boolean; data: UserWallet }>(
      `/wallet/bank-accounts/${accountId}/set-default`
    );
  },

  addMobileMoneyAccount: async (data: {
    provider: string;
    phoneNumber: string;
    accountName: string;
    isDefault?: boolean;
  }) => {
    return api.post<{ success: boolean; data: UserWallet }>('/wallet/mobile-money-accounts', data);
  },

  removeMobileMoneyAccount: async (accountId: string) => {
    return api.delete<{ success: boolean; message: string }>(
      `/wallet/mobile-money-accounts/${accountId}`
    );
  },

  setDefaultMobileMoneyAccount: async (accountId: string) => {
    return api.put<{ success: boolean; data: UserWallet }>(
      `/wallet/mobile-money-accounts/${accountId}/set-default`
    );
  },
};

export default walletApi;
